// ===================================================================
// ⚖️ GERENCIADOR DE RECURSOS - MÓDULO ADMIN
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { formatDate, exportToCSV } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';
import { getStatusBadgeStyle, getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortAppealsForDisplay,
  sortColumnIndicator,
} from '../../utils/adminTableSort';
import { AppealAnalysisModal } from '../../components/appeals/AppealAnalysisModal';
import {
  getAppealTypeLabel,
  getAnalysisHistoryTypeLabel,
  getAnalysisHistoryActionLabel,
  getHistoryScoreColumnLabel,
} from '../../constants/displayLabels';
import { INGRESSA_STORAGE_KEYS } from '../../utils/ingressaBrand';

const STORAGE_KEY_APPEAL = INGRESSA_STORAGE_KEYS.filtersAppeal;

export const AppealManager = React.memo(({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [appeals, setAppeals] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_APPEAL);
      if (s) { const p = JSON.parse(s); return p.filterStatus || ''; }
    } catch (e) {}
    return '';
  });
  const [filterType, setFilterType] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_APPEAL);
      if (s) { const p = JSON.parse(s); return p.filterType || ''; }
    } catch (e) {}
    return '';
  });
  const [filterCandidate, setFilterCandidate] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_APPEAL);
      if (s) {
        const p = JSON.parse(s);
        return p && p.filterCandidate ? String(p.filterCandidate) : '';
      }
    } catch (e) {}
    return '';
  });
  const [filterAnalyst, setFilterAnalyst] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_APPEAL);
      if (s) {
        const p = JSON.parse(s);
        const v = p && p.filterAnalyst ? String(p.filterAnalyst) : '';
        if (v === 'not_assigned') return '';
        return v;
      }
    } catch (e) {}
    return '';
  });
  const [filterCampus, setFilterCampus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_APPEAL);
      if (s) { const p = JSON.parse(s); return p && p.filterCampus ? String(p.filterCampus) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterCourse, setFilterCourse] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_APPEAL);
      if (s) { const p = JSON.parse(s); return p && p.filterCourse ? String(p.filterCourse) : ''; }
    } catch (e) {}
    return '';
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY_APPEAL, JSON.stringify({
        filterStatus,
        filterType,
        filterCandidate: String(filterCandidate || ''),
        filterAnalyst: String(filterAnalyst || ''),
        filterCampus: String(filterCampus || ''),
        filterCourse: String(filterCourse || ''),
      }));
    } catch (e) {}
  }, [filterStatus, filterType, filterCandidate, filterAnalyst, filterCampus, filterCourse]);

  const clearFilters = useCallback(() => {
    setFilterStatus('');
    setFilterType('');
    setFilterCandidate('');
    setFilterAnalyst('');
    setFilterCampus('');
    setFilterCourse('');
  }, []);

  /** Tipo canônico para o filtro (alinha sinônimos de lista). */
  const normalizeAppealTypeForFilter = useCallback((atype) => {
    const t = String(atype || 'exemption').toLowerCase();
    if (t === 'enrolled_list' || t === 'registration_list') return 'list';
    return t;
  }, []);

  const selectedProcess = useMemo(() => {
    const { id } = parseAdminSelectedProcessId(selectedProcessId);
    if (id == null) return null;
    return processes.find((p) => p.id === id) || null;
  }, [selectedProcessId, processes]);

  const historyScoreColumnLabel = getHistoryScoreColumnLabel(selectedProcess?.selection_mode);

  const getAppealTypeStyle = useCallback((type) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '9px',
      fontWeight: '500'
    };
    switch (type) {
      case 'exemption':
        return { ...baseStyle, backgroundColor: '#DBEAFE', color: '#1E40AF' };
      case 'list':
      case 'enrolled_list':
      case 'registration_list':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'preliminary_result':
        return { ...baseStyle, backgroundColor: '#E0E7FF', color: '#3730A3' };
      case 'answer_key':
        return { ...baseStyle, backgroundColor: '#FCE7F3', color: '#9D174D' };
      case 'special_attendance':
        return { ...baseStyle, backgroundColor: '#DCFCE7', color: '#166534' };
      case 'analysis':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      default:
        return baseStyle;
    }
  }, []);
  
  // Estados para seleção de analista
  const [showAnalystModal, setShowAnalystModal] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState('');
  const [selectedForBulkAppeal, setSelectedForBulkAppeal] = useState(new Set());
  const [bulkAppealAnalyst, setBulkAppealAnalyst] = useState('');
  const [equitableAppealAnalysts, setEquitableAppealAnalysts] = useState(new Set());
  const [showAppealAnalysisModal, setShowAppealAnalysisModal] = useState(false);
  const [resourceForAppealAnalysis, setResourceForAppealAnalysis] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [showViewAppealModal, setShowViewAppealModal] = useState(false);
  const [selectedAppealToView, setSelectedAppealToView] = useState(null);
  const [viewAppealTab, setViewAppealTab] = useState('details');
  const [appealAnalysisHistory, setAppealAnalysisHistory] = useState([]);
  const [appealHistoryLoading, setAppealHistoryLoading] = useState(false);

  const { addNotification } = useNotifications();
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [appealsData, analystsData, usersData, reasonsData] = await Promise.all([
        apiClient.getAppeals(),
        apiClient.getUsers('analyst'),
        apiClient.getUsers(),
        apiClient.getRejectionReasons()
      ]);

      setAppeals(Array.isArray(appealsData) ? appealsData : []);
      setAnalysts(Array.isArray(analystsData) ? analystsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assignAppealToAnalyst = useCallback((appealId) => {
    const appeal = appeals.find(a => a.id === appealId);
    if (!appeal) {
      setError('Recurso não encontrado');
      return;
    }

    setSelectedAppeal(appeal);
    setSelectedAnalyst('');
    setShowAnalystModal(true);
  }, [appeals]);

  const handleAppealAssignment = useCallback(async () => {
    if (!selectedAnalyst || !selectedAppeal) {
      setError('Selecione um analista');
      return;
    }

    try {
      const analyst = analysts.find(a => a.id === parseInt(String(selectedAnalyst), 10));
      if (!analyst) {
        setError('Analista não encontrado');
        return;
      }

      const { error: appealError } = await apiClient.updateAppeal(selectedAppeal.id, {
        status: 'assigned',
        analyst_id: analyst.id
      });
      if (appealError) throw new Error(appealError);

      if (selectedAppeal.application_id) {
        await apiClient.updateApplication(selectedAppeal.application_id, {
          status: 'appeal_assigned'
        });
      }

      addNotification(
        '✅ Recurso Atribuído',
        `Recurso enviado para análise do analista ${analyst.name}`,
        'success'
      );

      // Fechar modal e limpar estados
      setShowAnalystModal(false);
      setSelectedAppeal(null);
      setSelectedAnalyst('');

      await loadData();
      
    } catch (error) {
      console.error('❌ Erro ao atribuir recurso:', error);
      setError('Erro ao atribuir recurso');
      addNotification('Erro ao atribuir recurso', 'error');
    }
  }, [selectedAnalyst, selectedAppeal, analysts, addNotification, loadData]);

  const handleViewAppeal = useCallback((appeal) => {
    setSelectedAppealToView(appeal);
    setViewAppealTab('details');
    setShowViewAppealModal(true);
  }, []);

  const loadAppealAnalysisHistory = useCallback(async (applicationId) => {
    if (!applicationId) return;
    setAppealHistoryLoading(true);
    try {
      const data = await apiClient.getAnalysisHistory(applicationId);
      setAppealAnalysisHistory(Array.isArray(data) ? data : []);
    } catch {
      setAppealAnalysisHistory([]);
    } finally {
      setAppealHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showViewAppealModal && selectedAppealToView && viewAppealTab === 'history') {
      loadAppealAnalysisHistory(selectedAppealToView.application_id);
    }
  }, [showViewAppealModal, selectedAppealToView?.application_id, viewAppealTab, loadAppealAnalysisHistory]);

  useEffect(() => {
    if (!showViewAppealModal || !selectedAppealToView?.id) return;
    const fresh = appeals.find((x) => x.id === selectedAppealToView.id);
    if (!fresh) return;
    const changed =
      fresh.analyst_id !== selectedAppealToView.analyst_id ||
      (fresh.status || '') !== (selectedAppealToView.status || '') ||
      (fresh.analyst_notes || '') !== (selectedAppealToView.analyst_notes || '') ||
      (fresh.analyst_name || '') !== (selectedAppealToView.analyst_name || '') ||
      (fresh.appeal_response_published_at || '') !== (selectedAppealToView.appeal_response_published_at || '');
    if (!changed) return;
    setSelectedAppealToView(fresh);
    if (viewAppealTab === 'history' && fresh.application_id) {
      loadAppealAnalysisHistory(fresh.application_id);
    }
  }, [appeals, showViewAppealModal, selectedAppealToView, viewAppealTab, loadAppealAnalysisHistory]);

  const handleDownloadAppealDocument = useCallback(async (appeal, index) => {
    const doc = appeal.documents?.[index] || {};
    const name = doc.name || `documento_${index + 1}.pdf`;
    const docUrl = doc.url;
    if (docUrl && (docUrl.startsWith('/static/') || docUrl.startsWith('http'))) {
      const a = document.createElement('a');
      a.href = docUrl.startsWith('/') ? `${window.location.origin}${docUrl}` : docUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      addNotification('Download', 'Download iniciado', 'success');
      return;
    }
    try {
      const blob = await apiClient.downloadDocument({ appealId: appeal.id, index });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addNotification('Download', 'Documento baixado', 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao baixar', 'error');
    }
  }, [addNotification]);

  const handleAnalyzeAppeal = useCallback((appeal) => {
    setResourceForAppealAnalysis(appeal);
    setShowAppealAnalysisModal(true);
  }, []);

  const handleCloseAppealAnalysis = useCallback(() => {
    setShowAppealAnalysisModal(false);
    setResourceForAppealAnalysis(null);
  }, []);

  const analystsForProcess = useMemo(() => {
    const { empty, invalid, id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty) return analysts;
    if (invalid || pid == null) return [];
    return analysts.filter(a => {
      const pids = a.process_ids || [];
      return pids.length === 0 || pids.includes(pid);
    });
  }, [analysts, selectedProcessId]);

  const appealAnalystFilterOptions = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return [];
    const byId = new Map();
    analystsForProcess.forEach((a) => byId.set(String(a.id), { id: a.id, name: a.name || `Usuário #${a.id}` }));
    appeals.forEach((ap) => {
      if (Number(ap.process_id) !== pid || !ap.analyst_id) return;
      const id = ap.analyst_id;
      const key = String(id);
      if (byId.has(key)) return;
      let name = ap.analyst_name;
      if (!name) {
        const u = users.find((u) => Number(u.id) === Number(id));
        if (u) name = u.role === 'admin' ? (u.name || 'Administrador') : (u.name || `Usuário #${id}`);
        else name = `Usuário #${id}`;
      }
      byId.set(key, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [selectedProcessId, analystsForProcess, appeals, users]);

  useEffect(() => {
    if (!filterAnalyst || !selectedProcessId) return;
    const ok = appealAnalystFilterOptions.some((o) => String(o.id) === String(filterAnalyst));
    if (!ok) setFilterAnalyst('');
  }, [filterAnalyst, selectedProcessId, appealAnalystFilterOptions]);

  const appealAnalystCellLabel = useCallback((appeal) => {
    if (appeal.analyst_name) return appeal.analyst_name;
    if (!appeal.analyst_id) return 'Não atribuído';
    const u = users.find((u) => Number(u.id) === Number(appeal.analyst_id));
    if (u) return u.role === 'admin' ? (u.name || 'Administrador') : (u.name || `Usuário #${appeal.analyst_id}`);
    return `Usuário #${appeal.analyst_id}`;
  }, [users]);

  const appealStatusExportLabel = useCallback((status) => {
    const s = status || '';
    if (s === 'pending_assignment' || s === 'pending') return 'Pendente de Atribuição';
    if (s === 'assigned') return 'Atribuído';
    if (s === 'approved') return 'Aprovado';
    if (s === 'rejected') return 'Rejeitado';
    return s || '—';
  }, []);

  const appealBadgeStatusKey = useCallback((status) => {
    const s = status || '';
    if (s === 'pending_assignment' || s === 'pending') return 'appeal_pending_assignment';
    if (s === 'assigned') return 'appeal_assigned';
    if (s === 'approved') return 'appeal_approved';
    if (s === 'rejected') return 'appeal_rejected';
    return s;
  }, []);

  const filteredAppeals = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    return appeals.filter((appeal) => {
      if (!procSel.empty) {
        if (procSel.invalid) return false;
        if (appeal.process_id !== procSel.id) return false;
      }
      if (filterStatus && appeal.status !== filterStatus) return false;
      if (filterType && normalizeAppealTypeForFilter(appeal.appeal_type) !== filterType) return false;
      const q = (filterCandidate || '').trim().toLowerCase();
      if (q && ![appeal.candidate_name, appeal.candidate_cpf].some((v) => v && String(v).toLowerCase().includes(q))) return false;
      if (filterAnalyst && String(appeal.analyst_id || '') !== String(filterAnalyst)) return false;
      if (filterCampus && String(appeal.course_campus || '') !== String(filterCampus)) return false;
      if (filterCourse && String(appeal.course_name || '') !== String(filterCourse)) return false;
      return true;
    });
  }, [appeals, selectedProcessId, filterStatus, filterType, filterCandidate, filterAnalyst, filterCampus, filterCourse, normalizeAppealTypeForFilter]);

  const sortedAppeals = useMemo(
    () => sortAppealsForDisplay(filteredAppeals, tableSort),
    [filteredAppeals, tableSort]
  );

  const campusFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const campuses = new Set();
    appeals.forEach((appeal) => {
      if (!procSel.empty) {
        if (procSel.invalid) return;
        if (appeal.process_id !== procSel.id) return;
      }
      if (appeal.course_campus) campuses.add(String(appeal.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [appeals, selectedProcessId]);

  const courseFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const courses = new Set();
    appeals.forEach((appeal) => {
      if (!procSel.empty) {
        if (procSel.invalid) return;
        if (appeal.process_id !== procSel.id) return;
      }
      if (appeal.course_name) courses.add(String(appeal.course_name));
    });
    return Array.from(courses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [appeals, selectedProcessId]);

  const exportAppealsData = useCallback(() => {
    const data = sortedAppeals.map((appeal) => ({
      'Nome do Candidato': appeal.candidate_name || 'N/A',
      'CPF': appeal.candidate_cpf || 'N/A',
      'Curso': appeal.course_name || 'N/A',
      'Unidade': appeal.course_campus || '—',
      'Tipo de Recurso': getAppealTypeLabel(appeal.appeal_type || 'exemption'),
      'Situação': appealStatusExportLabel(appeal.status),
      'Analista': appealAnalystCellLabel(appeal),
      'Data de Criação': formatDate(appeal.created_at)
    }));

    exportToCSV(data, 'recursos_ingressa.csv');
    addNotification('Relatório exportado com sucesso!', 'success');
  }, [sortedAppeals, addNotification, getAppealTypeLabel, appealStatusExportLabel, appealAnalystCellLabel]);

  const pendingAppealsForBulk = useMemo(() => {
    return filteredAppeals.filter((a) => {
      const s = (a.status || '').toLowerCase();
      return s === 'pending_assignment' || s === 'pending';
    });
  }, [filteredAppeals]);

  const handleBulkAssignAppeals = useCallback(async () => {
    if (!bulkAppealAnalyst || selectedForBulkAppeal.size === 0) {
      addNotification('Selecione um analista e ao menos um recurso', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulkAppeal);
    try {
      const { data, error } = await apiClient.bulkAssignAppeals(ids, parseInt(bulkAppealAnalyst, 10));
      if (error) throw new Error(error);
      const count = data?.assigned_count ?? 0;
      const analyst = analystsForProcess.find(a => a.id === parseInt(String(bulkAppealAnalyst), 10));
      addNotification('Atribuição em massa', `${count} recurso(s) atribuído(s) a ${analyst?.name || 'analista'}`, 'success');
      setSelectedForBulkAppeal(new Set());
      setBulkAppealAnalyst('');
      await loadData();
    } catch (err) {
      addNotification('Erro ao atribuir', err.message || 'Erro', 'error');
    }
  }, [bulkAppealAnalyst, selectedForBulkAppeal, analystsForProcess, addNotification, loadData]);

  const handleBulkAssignAppealsEquitable = useCallback(async () => {
    if (selectedForBulkAppeal.size === 0 || equitableAppealAnalysts.size < 2) {
      addNotification('Atenção', 'Selecione recursos e marque ao menos 2 analistas para distribuição equânime.', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulkAppeal);
    const analystIds = Array.from(equitableAppealAnalysts)
      .map((x) => parseInt(x, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    try {
      const { data, error } = await apiClient.bulkAssignAppealsEquitable(ids, analystIds);
      if (error) throw new Error(error);
      const count = data?.assigned_count ?? 0;
      const per = data?.per_analyst || {};
      const parts = Object.entries(per)
        .filter(([, c]) => c > 0)
        .map(([id, c]) => {
          const an = analystsForProcess.find((x) => String(x.id) === String(id));
          return `${an?.name || id}: ${c}`;
        });
      addNotification(
        'Distribuição equânime',
        `${count} recurso(s) atribuído(s)${parts.length ? ` — ${parts.join('; ')}` : ''}`,
        'success'
      );
      setSelectedForBulkAppeal(new Set());
      setEquitableAppealAnalysts(new Set());
      await loadData();
    } catch (err) {
      addNotification('Erro ao distribuir', err.message || 'Erro', 'error');
    }
  }, [selectedForBulkAppeal, equitableAppealAnalysts, analystsForProcess, addNotification, loadData]);

  useEffect(() => {
    setSelectedForBulkAppeal(new Set());
    setBulkAppealAnalyst('');
    setEquitableAppealAnalysts(new Set());
    setFilterAnalyst('');
    setFilterCampus('');
    setFilterCourse('');
    setTableSort(initialAdminTableSort());
  }, [selectedProcessId]);

  const appealPublishStats = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    let unpublishedDecided = 0;
    appeals.forEach((ap) => {
      if (!procSel.empty) {
        if (procSel.invalid) return;
        if (ap.process_id !== procSel.id) return;
      }
      const st = (ap.status || '').toLowerCase();
      if (!['approved', 'rejected'].includes(st)) return;
      if (!ap.show_appeal_response_to_candidates) return;
      if (ap.appeal_response_published_at) return;
      unpublishedDecided += 1;
    });
    let currentProc = null;
    if (!procSel.empty && !procSel.invalid) {
      currentProc = processes.find((p) => p.id === procSel.id) || null;
    }
    const procAllows = Boolean(currentProc?.show_appeal_response_to_candidates);
    return { unpublishedDecided, procAllows, currentProc };
  }, [appeals, selectedProcessId, processes]);

  const handlePublishAppealResponse = useCallback(async () => {
    const a = selectedAppealToView;
    if (!a) return;
    try {
      const { data, error } = await apiClient.publishAppealResponseToCandidate(a.id);
      if (error) throw new Error(error);
      addNotification('Publicado', 'Resposta visível ao candidato em Ver recurso.', 'success');
      setSelectedAppealToView((prev) => (prev && prev.id === a.id
        ? { ...prev, appeal_response_published_at: data?.appeal_response_published_at || new Date().toISOString() }
        : prev));
      await loadData();
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao publicar', 'error');
    }
  }, [selectedAppealToView, addNotification, loadData]);

  const handleBulkPublishAppealResponses = useCallback(async () => {
    const pid = appealPublishStats.currentProc?.id;
    if (!pid) {
      addNotification('Atenção', 'Selecione um processo.', 'warning');
      return;
    }
    if (!window.confirm(`Publicar resposta ao candidato para todos os recursos já decididos e ainda não publicados neste processo? (${appealPublishStats.unpublishedDecided} recurso(s))`)) return;
    try {
      const { data, error } = await apiClient.bulkPublishAppealResponses(pid);
      if (error) throw new Error(error);
      addNotification('Concluído', `${data?.published_count ?? 0} recurso(s) publicado(s).`, 'success');
      await loadData();
    } catch (err) {
      addNotification('Erro', err.message || 'Falha na publicação em massa', 'error');
    }
  }, [appealPublishStats, addNotification, loadData]);

  const handlePrintAppealOfficialPdf = useCallback(() => {
    const a = selectedAppealToView;
    if (!a) return;
    const proc = processes.find((p) => p.id === a.process_id);
    const st = (a.status || '').toLowerCase();
    const decision = st === 'approved' ? 'Deferido' : st === 'rejected' ? 'Indeferido' : (a.status || '—');
    const pubNote = a.appeal_response_published_at
      ? `<p><em>Visível ao candidato no portal (publicado em ${formatDate(a.appeal_response_published_at)}).</em></p>`
      : '<p><em><strong>Rascunho:</strong> pode não estar visível ao candidato até você publicar a resposta no sistema.</em></p>';
    const fund = (a.analyst_notes || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Resposta ao recurso</title>
<style>body{font-family:system-ui,sans-serif;max-width:720px;margin:24px auto;line-height:1.5} h1{font-size:18px} .box{border:1px solid #ccc;padding:12px;margin:12px 0;border-radius:8px;background:#fafafa}</style></head><body>
<h1>Resposta ao recurso — Ingressa</h1>
<p><strong>Processo:</strong> ${(proc?.name || '—').replace(/</g, '&lt;')}</p>
<p><strong>Inscrição:</strong> #${a.application_id}</p>
<p><strong>Candidato:</strong> ${String(a.candidate_name || '—').replace(/</g, '&lt;')}</p>
<p><strong>CPF:</strong> ${String(a.candidate_cpf || '—').replace(/</g, '&lt;')}</p>
<p><strong>Tipo de recurso:</strong> ${getAppealTypeLabel(a.appeal_type || 'exemption').replace(/</g, '&lt;')}</p>
<div class="box"><strong>Decisão:</strong> ${decision}</div>
<div class="box"><strong>Fundamentação:</strong><br/>${fund || '(não registrada)'}</div>
${pubNote}
<p style="font-size:12px;color:#666">Gerado em ${new Date().toLocaleString('pt-BR')} — use &quot;Salvar como PDF&quot; na janela de impressão do navegador.</p>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) {
      addNotification('Pop-up bloqueado', 'Permita pop-ups para gerar o PDF.', 'warning');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  }, [selectedAppealToView, processes, getAppealTypeLabel, addNotification]);

  if (loading) {
    return <LoadingSpinner text="Carregando dados de recursos..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={{...styles.flexBetween, ...styles.mb6}}>
        <div>
          <h2 style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            color: '#111827', 
            marginBottom: '8px' 
          }}>
            ⚖️ Gerenciar Recursos
          </h2>
          <p style={{ fontSize: '16px', color: '#6B7280' }}>
            Controle de recursos (isenção, lista de inscritos, resultado preliminar)
          </p>
        </div>
        <div style={{...styles.flex, ...styles.gap4}}>
          <button
            onClick={exportAppealsData}
            style={{...styles.button, ...styles.buttonInfo}}
          >
            📄 Exportar Relatório
          </button>
        </div>
      </div>

      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <label style={styles.label}>Processo seletivo:</label>
        <select
          value={selectedProcessId}
          onChange={setSelectedProcessId}
          style={{ ...styles.select, maxWidth: '400px' }}
        >
          <option value="">Selecione o processo</option>
          {processes.map((p) => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>
      </div>

      {appealPublishStats.procAllows && appealPublishStats.unpublishedDecided > 0 && (
        <div style={{
          ...styles.card,
          marginBottom: '16px',
          padding: '14px 16px',
          backgroundColor: '#FFFBEB',
          border: '1px solid #FCD34D',
          borderRadius: '8px',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '14px', color: '#92400E' }}>
              <strong>Respostas ao candidato:</strong> há {appealPublishStats.unpublishedDecided} recurso(s) já decidido(s) neste processo sem publicação em &quot;Ver recurso&quot;.
            </div>
            <button
              type="button"
              onClick={handleBulkPublishAppealResponses}
              style={{ ...styles.button, ...styles.buttonPrimary, whiteSpace: 'nowrap' }}
            >
              Publicar todos (deste processo)
            </button>
          </div>
        </div>
      )}

      <div style={{...styles.card, ...styles.mb6}}>
        <div style={{ ...styles.flexBetween, marginBottom: '16px' }}>
          <h3 style={{ ...styles.cardTitle, margin: 0 }}>Filtros</h3>
          <button
            onClick={clearFilters}
            style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px', flexShrink: 0 }}
            title="Limpar todos os filtros"
          >
            🗑️ Limpar filtros
          </button>
        </div>
        <div style={{...styles.flex, ...styles.gap4, ...styles.flexWrap, alignItems: 'flex-end'}}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Candidato:</label>
            <input
              type="text"
              value={filterCandidate}
              onChange={(e) => setFilterCandidate(e.target.value)}
              placeholder="Nome, CPF..."
              style={{ ...styles.select, fontFamily: 'inherit' }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Analista:</label>
            <select
              value={filterAnalyst}
              onChange={(e) => setFilterAnalyst(e.target.value)}
              style={{
                ...styles.select,
                opacity: selectedProcessId ? 1 : 0.65,
              }}
              disabled={!selectedProcessId}
              title={
                selectedProcessId
                  ? 'Analistas do processo ou já atribuídos a recursos neste processo (inclui admin)'
                  : 'Selecione o processo seletivo para habilitar este filtro'
              }
            >
              <option value="">{selectedProcessId ? 'Todos' : 'Selecione o processo…'}</option>
              {appealAnalystFilterOptions.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Tipo:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={styles.select}
            >
              <option value="">Todos os tipos</option>
              <option value="exemption">Isenção</option>
              <option value="list">Lista de inscritos</option>
              <option value="preliminary_result">Resultado preliminar</option>
              <option value="answer_key">Gabarito</option>
              <option value="special_attendance">Atendimento especial</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Situação:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas as situações</option>
              <option value="pending_assignment">Pendente de Atribuição</option>
              <option value="assigned">Atribuído</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidade:</label>
            <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
              <option value="">Todas as unidades</option>
              {campusFilterOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Curso:</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
              <option value="">Todos os cursos</option>
              {courseFilterOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{...styles.flexBetween, ...styles.mb4, flexWrap: 'wrap', gap: 12}}>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Recursos ({filteredAppeals.length})
          </h3>
          {pendingAppealsForBulk.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10, maxWidth: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setSelectedForBulkAppeal(new Set(pendingAppealsForBulk.map(a => a.id)))} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}>
                  Selecionar todos ({pendingAppealsForBulk.length})
                </button>
                <button type="button" onClick={() => setSelectedForBulkAppeal(new Set())} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}>
                  Desmarcar
                </button>
                <select value={bulkAppealAnalyst} onChange={(e) => setBulkAppealAnalyst(e.target.value)} style={{ ...styles.select, padding: '6px 10px', minWidth: 180 }}>
                  <option value="">Atribuir a...</option>
                  {analystsForProcess.map(a => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                </select>
                <button type="button" onClick={handleBulkAssignAppeals} disabled={!bulkAppealAnalyst || selectedForBulkAppeal.size === 0} style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12 }}>
                  Atribuir selecionados ({selectedForBulkAppeal.size})
                </button>
              </div>
              <div style={{ paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 }}>Distribuição equânime (rodízio)</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>Marque 2 ou mais analistas.</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: 8 }}>
                  {analystsForProcess.map((a) => (
                    <label key={a.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={equitableAppealAnalysts.has(String(a.id))}
                        onChange={(e) => {
                          setEquitableAppealAnalysts((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(String(a.id));
                            else next.delete(String(a.id));
                            return next;
                          });
                        }}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button type="button" onClick={() => setEquitableAppealAnalysts(new Set(analystsForProcess.map((x) => String(x.id))))} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}>
                    Marcar todos os analistas do processo
                  </button>
                  <button type="button" onClick={() => setEquitableAppealAnalysts(new Set())} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}>
                    Limpar marcação
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkAssignAppealsEquitable}
                    disabled={selectedForBulkAppeal.size === 0 || equitableAppealAnalysts.size < 2}
                    style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12, opacity: (selectedForBulkAppeal.size === 0 || equitableAppealAnalysts.size < 2) ? 0.55 : 1 }}
                  >
                    Distribuir ({selectedForBulkAppeal.size} recursos)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert type="error" message={error} onClose={() => setError('')} />
        )}

        {filteredAppeals.length === 0 ? (
          <div style={{...styles.textCenter, ...styles.py8}}>
            <p style={{ color: '#6B7280', fontSize: '16px' }}>
              Nenhum recurso encontrado com os filtros aplicados.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{...styles.table, width: '100%', fontSize: '10px', hyphens: 'none'}}>
              <thead>
                <tr>
                  {pendingAppealsForBulk.length > 0 && (
                    <th style={{...styles.th, width: '36px', fontSize: '9px', padding: '8px 4px'}} title="Selecionar para atribuição em massa">☐</th>
                  )}
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'id' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.th, width: '45px', fontSize: '9px', padding: '8px 6px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((p) => nextAdminTableSort(p, 'id'))}
                    title="Ordenar por número da inscrição"
                  >
                    Nº{sortColumnIndicator(tableSort, 'id')}
                  </th>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'candidate' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.th, width: '16%', fontSize: '10px', padding: '8px 10px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
                    title="Ordenar por nome do candidato"
                  >
                    Candidato{sortColumnIndicator(tableSort, 'candidate')}
                  </th>
                  <th style={{...styles.th, width: '18%', fontSize: '10px', padding: '8px 10px'}}>Curso</th>
                  <th style={{...styles.th, width: '12%', fontSize: '10px', padding: '8px 10px'}}>Unidade</th>
                  <th style={{...styles.th, width: '12%', fontSize: '10px', padding: '8px 10px'}}>Tipo de Recurso</th>
                  <th style={{...styles.th, minWidth: '150px', width: '150px', fontSize: '9px', padding: '8px 8px', textAlign: 'center', verticalAlign: 'middle'}}>Situação</th>
                  <th style={{...styles.th, width: '12%', fontSize: '10px', padding: '8px 10px'}}>Analista</th>
                  <th style={{...styles.th, width: '12%', fontSize: '10px', padding: '8px 10px'}}>Data</th>
                  <th style={{...styles.th, minWidth: '100px', fontSize: '10px', padding: '8px 10px', textAlign: 'center'}}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedAppeals.map(appeal => {
                  const isPendingForBulk = ['pending_assignment', 'pending'].includes((appeal.status || '').toLowerCase());
                  return (
                    <tr key={appeal.id} style={styles.tr}>
                      {pendingAppealsForBulk.length > 0 && (
                        <td style={{...styles.td, padding: '6px 4px', width: '36px', textAlign: 'center'}}>
                          {isPendingForBulk ? (
                            <input
                              type="checkbox"
                              checked={selectedForBulkAppeal.has(appeal.id)}
                              onChange={(e) => {
                                setSelectedForBulkAppeal(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(appeal.id);
                                  else next.delete(appeal.id);
                                  return next;
                                });
                              }}
                            />
                          ) : null}
                        </td>
                      )}
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 6px', fontWeight: '600', color: '#374151'}}>
                        #{appeal.application_id}
                      </td>
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 10px'}}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                            {appeal.candidate_name || 'N/A'}
                          </div>
                          <div style={{ fontSize: '9px', color: '#6B7280' }}>
                            CPF: {appeal.candidate_cpf || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 10px'}}>
                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                          {appeal.course_name || 'N/A'}
                        </div>
                      </td>
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 10px'}}>{appeal.course_campus || '—'}</td>
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 10px'}}>
                        <span style={{...getAppealTypeStyle(appeal.appeal_type || 'exemption'), fontSize: '9px', padding: '4px 8px'}}>
                          {getAppealTypeLabel(appeal.appeal_type || 'exemption')}
                          {appeal.appeal_type === 'answer_key' && appeal.question_number
                            ? ` — Q. ${appeal.question_number}`
                            : ''}
                        </span>
                      </td>
                      <td style={{
                        ...styles.tableCell,
                        verticalAlign: 'middle',
                        minWidth: '150px',
                        width: '150px',
                        maxWidth: '180px',
                        whiteSpace: 'normal',
                        padding: '8px 8px',
                        textAlign: 'center',
                        fontSize: '10px',
                      }}>
                        <span
                          style={{
                            ...getStatusBadgeStyle(appealBadgeStatusKey(appeal.status)),
                            ...styles.tableCellBadge,
                            fontSize: '9px',
                            padding: '6px 10px',
                            lineHeight: 1.35,
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                          }}
                          title={appealStatusExportLabel(appeal.status)}
                        >
                          {appealStatusExportLabel(appeal.status)}
                        </span>
                      </td>
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 10px'}}>
                        {appealAnalystCellLabel(appeal)}
                      </td>
                      <td style={{...styles.td, fontSize: '10px', padding: '8px 10px'}}>
                        {formatDate(appeal.created_at)}
                      </td>
                      <td style={{...styles.td, textAlign: 'center', minWidth: '100px', whiteSpace: 'normal', padding: '8px 10px'}}>
                        {isPendingForBulk && (
                          <>
                            <button
                              onClick={() => assignAppealToAnalyst(appeal.id)}
                              style={{ ...getButtonStyle('assignment'), padding: '3px 6px', fontSize: '9px' }}
                            >
                              {ACTION_ICONS.assign} Atribuir
                            </button>
                            {!appeal.analyst_id && (
                              <button
                                onClick={() => handleAnalyzeAppeal(appeal)}
                                style={{ ...getButtonStyle('primary', 'small'), padding: '3px 6px', fontSize: '9px', marginLeft: '6px' }}
                                title="Analisar recurso"
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            )}
                          </>
                        )}
                        {appeal.status === 'assigned' && (
                          <span style={{...getStatusBadgeStyle('assigned'), ...styles.tableCellBadge, fontSize: '9px', padding: '4px 8px'}}>
                            Atribuído
                          </span>
                        )}
                        {(appeal.status === 'approved' || appeal.status === 'rejected') && (
                          <button
                            type="button"
                            onClick={() => handleViewAppeal(appeal)}
                            title="Ver detalhes e histórico (somente leitura)"
                            style={{
                              padding: '3px 6px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: '#DBEAFE',
                              color: '#1D4ED8',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            {ACTION_ICONS.view}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Seleção de Analista */}
      {showAnalystModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '500px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937'
            }}>
              🎫 Atribuir Recurso para Análise
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
                <strong>Candidato:</strong> {selectedAppeal?.candidate_name}
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
                <strong>CPF:</strong> {selectedAppeal?.candidate_cpf}
              </p>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6B7280' }}>
                <strong>Curso:</strong> {selectedAppeal?.course_name}<br/>
                <strong>Unidade:</strong> {selectedAppeal?.course_campus || '—'}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Selecione o Analista:
              </label>
              <select
                value={selectedAnalyst}
                onChange={(e) => setSelectedAnalyst(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Escolha um analista...</option>
                {analystsForProcess.map(analyst => (
                  <option key={analyst.id} value={analyst.id}>
                    {analyst.name} ({analyst.email})
                  </option>
                ))}
              </select>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowAnalystModal(false);
                  setSelectedAppeal(null);
                  setSelectedAnalyst('');
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleAppealAssignment}
                disabled={!selectedAnalyst}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: selectedAnalyst ? '#3B82F6' : '#9CA3AF',
                  color: 'white',
                  fontSize: '14px',
                  cursor: selectedAnalyst ? 'pointer' : 'not-allowed'
                }}
              >
                Atribuir para Análise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Recurso (somente leitura) — igual conceito a Pagamentos / Análise */}
      <Modal
        isOpen={showViewAppealModal}
        onClose={() => {
          setShowViewAppealModal(false);
          setSelectedAppealToView(null);
          setViewAppealTab('details');
          setAppealAnalysisHistory([]);
        }}
        title={selectedAppealToView ? `${ACTION_ICONS.view} Detalhes do Recurso – ${selectedAppealToView.candidate_name}` : 'Detalhes do Recurso'}
        large
      >
        {selectedAppealToView && (
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #E5E7EB' }}>
              <button
                type="button"
                onClick={() => setViewAppealTab('details')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: viewAppealTab === 'details' ? '#3B82F6' : 'transparent',
                  color: viewAppealTab === 'details' ? 'white' : '#6B7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Detalhes
              </button>
              <button
                type="button"
                onClick={() => setViewAppealTab('history')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: viewAppealTab === 'history' ? '#3B82F6' : 'transparent',
                  color: viewAppealTab === 'history' ? 'white' : '#6B7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Histórico
              </button>
            </div>

            {viewAppealTab === 'details' && (
              <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                  <button type="button" onClick={handlePrintAppealOfficialPdf} style={{ ...styles.button, ...styles.buttonSecondary }}>
                    📄 Salvar PDF (imprimir)
                  </button>
                  {selectedAppealToView.show_appeal_response_to_candidates &&
                    ['approved', 'rejected'].includes((selectedAppealToView.status || '').toLowerCase()) && (
                    selectedAppealToView.appeal_response_published_at ? (
                      <span style={{ fontSize: '13px', color: '#059669' }}>
                        Publicado ao candidato em {formatDate(selectedAppealToView.appeal_response_published_at)}
                      </span>
                    ) : (
                      <button type="button" onClick={handlePublishAppealResponse} style={{ ...styles.button, ...styles.buttonPrimary }}>
                        Publicar resposta ao candidato
                      </button>
                    )
                  )}
                  {selectedAppealToView.show_appeal_response_to_candidates === false &&
                    ['approved', 'rejected'].includes((selectedAppealToView.status || '').toLowerCase()) && (
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>
                      O processo não está configurado para exibir resposta no portal do candidato (cadastro do processo).
                    </span>
                  )}
                </div>
                <p><strong>Inscrição Nº:</strong> #{selectedAppealToView.application_id}</p>
                <p><strong>Candidato:</strong> {selectedAppealToView.candidate_name || 'N/A'}</p>
                <p><strong>CPF:</strong> {selectedAppealToView.candidate_cpf || 'N/A'}</p>
                <p><strong>Processo:</strong> {processes.find((p) => p.id === selectedAppealToView.process_id)?.name || 'N/A'}</p>
                <p><strong>Curso:</strong> {selectedAppealToView.course_name || 'N/A'}</p>
                <p><strong>Unidade:</strong> {selectedAppealToView.course_campus || '—'}</p>
                <p><strong>Tipo de recurso:</strong> {getAppealTypeLabel(selectedAppealToView.appeal_type || 'exemption')}</p>
                {selectedAppealToView.appeal_type === 'answer_key' && selectedAppealToView.question_number && (
                  <p><strong>Questão:</strong> {selectedAppealToView.question_number}</p>
                )}
                <p><strong>Situação do recurso:</strong> {appealStatusExportLabel(selectedAppealToView.status)}</p>
                <p><strong>Analista:</strong> {appealAnalystCellLabel(selectedAppealToView)}</p>
                <p><strong>Data do recurso:</strong> {formatDate(selectedAppealToView.created_at)}</p>
                {(selectedAppealToView.narrative || selectedAppealToView.justification) && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    <strong>Narrativa / justificativa do candidato</strong>
                    <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{selectedAppealToView.narrative || selectedAppealToView.justification}</p>
                  </div>
                )}
                {selectedAppealToView.analyst_notes && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#ECFDF5', borderRadius: '8px', border: '1px solid #A7F3D0' }}>
                    <strong>Decisão — fundamentação</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6B7280' }}>Texto que poderá ser exibido ao candidato após publicação.</p>
                    <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{selectedAppealToView.analyst_notes}</p>
                  </div>
                )}
                {selectedAppealToView.appeal_return_reason && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px' }}>
                    <strong>Motivo de devolução (ao admin)</strong>
                    <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{selectedAppealToView.appeal_return_reason}</p>
                  </div>
                )}
                <div style={{ marginTop: '16px' }}>
                  <strong>Documentos do recurso</strong>
                  {selectedAppealToView.documents?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                      {selectedAppealToView.documents.map((doc, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                          <span style={{ fontSize: '13px' }}>{doc.name || doc.url || `Documento ${idx + 1}`}</span>
                          <button
                            type="button"
                            onClick={() => handleDownloadAppealDocument(selectedAppealToView, idx)}
                            style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 10px', fontSize: '12px' }}
                          >
                            Baixar
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#6B7280', fontStyle: 'italic' }}>Nenhum documento anexado</p>
                  )}
                </div>
              </div>
            )}

            {viewAppealTab === 'history' && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
                  Histórico de análises da inscrição Nº {selectedAppealToView.application_id} (mesmo critério de Pagamentos / Análise).
                </p>
                {appealHistoryLoading ? (
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>Carregando histórico...</p>
                ) : appealAnalysisHistory.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>Nenhum registro de histórico.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Data</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Analista</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Tipo</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Ação</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>{historyScoreColumnLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appealAnalysisHistory.map((r, i) => (
                          <tr key={r.id || i} style={{ borderBottom: '1px solid #E5E7EB' }}>
                            <td style={{ padding: '8px' }}>{r.created_at ? formatDate(r.created_at) : '—'}</td>
                            <td style={{ padding: '8px' }}>{r.analyst_name || '—'}</td>
                            <td style={{ padding: '8px' }}>{getAnalysisHistoryTypeLabel(r.analysis_type, r.analysis_type_label)}</td>
                            <td style={{ padding: '8px' }}>{getAnalysisHistoryActionLabel(r.action, r.action_label)}</td>
                            <td style={{ padding: '8px' }}>{r.final_score != null ? parseFloat(r.final_score).toFixed(2) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
              <button
                type="button"
                onClick={() => {
                  setShowViewAppealModal(false);
                  setSelectedAppealToView(null);
                  setViewAppealTab('details');
                  setAppealAnalysisHistory([]);
                }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      <AppealAnalysisModal
        isOpen={showAppealAnalysisModal}
        onClose={handleCloseAppealAnalysis}
        resource={resourceForAppealAnalysis}
        user={user}
        rejectionReasons={rejectionReasons}
        addNotification={addNotification}
        onSaved={loadData}
      />
    </div>
  );
});

export default AppealManager;
